import React, { useState, useEffect } from 'react';
import { Upload, Check, AlertCircle, X, Loader2, ExternalLink } from 'lucide-react';
import { formatDuration, getTotalDuration } from '../utils/formatters';
import { useJiraSync } from '../hooks';

export function ReportsView({ entries, projects, getProjectById }) {
  const { loading, syncToJira, getConfig } = useJiraSync();
  const [jiraConfigured, setJiraConfigured] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Check if Jira is configured
  useEffect(() => {
    async function checkConfig() {
      try {
        const result = await getConfig();
        setJiraConfigured(result.configured);
      } catch (err) {
        console.error('Error checking Jira config:', err);
      }
    }
    checkConfig();
  }, [getConfig]);

  // Count entries that could be synced
  const unsyncedEntries = entries.filter(entry =>
    entry.endTime && !entry.jiraSyncedAt && entry.description
  );

  const handleSync = async () => {
    setSyncResult(null);
    setShowSyncModal(true);
    try {
      const result = await syncToJira();
      setSyncResult(result);
    } catch (err) {
      setSyncResult({
        success: false,
        error: err.message,
        summary: { total: 0, synced: 0, skipped: 0, failed: 0 },
        results: []
      });
    }
  };
  const getReportData = () => {
    const projectStats = {};
    const projectIds = new Set(projects.map(p => p.id));

    entries.forEach(entry => {
      const entryProjectId = entry.projectId || 'no-project';
      // Group unknown projects together
      const projectId = projectIds.has(entryProjectId) ? entryProjectId : 'unknown';
      const duration = entry.endTime - entry.startTime;

      if (!projectStats[projectId]) {
        projectStats[projectId] = {
          project: projectId === 'unknown'
            ? { id: 'unknown', name: 'Unknown', color: '#94a3b8' }
            : getProjectById(projectId),
          duration: 0,
          count: 0
        };
      }

      projectStats[projectId].duration += duration;
      projectStats[projectId].count += 1;
    });

    return Object.values(projectStats).sort((a, b) => b.duration - a.duration);
  };

  if (entries.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Time Reports</h2>
        </div>
        <div className="text-center py-12 text-gray-400">
          No time entries to report yet
        </div>
      </div>
    );
  }

  const reportData = getReportData();
  const totalDuration = getTotalDuration(entries);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Time Reports</h2>
        {jiraConfigured ? (
          <button
            onClick={handleSync}
            disabled={loading || unsyncedEntries.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium"
          >
            {loading ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <Upload size={18} />
            )}
            Sync to Jira
            {unsyncedEntries.length > 0 && (
              <span className="bg-blue-500 px-2 py-0.5 rounded-full text-xs">
                {unsyncedEntries.length}
              </span>
            )}
          </button>
        ) : (
          <a
            href="/settings"
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
          >
            <ExternalLink size={18} />
            Configure Jira
          </a>
        )}
      </div>

      {/* Sync Result Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {loading ? 'Syncing to Jira...' : 'Sync Results'}
              </h3>
              {!loading && (
                <button
                  onClick={() => setShowSyncModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              )}
            </div>

            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {loading && !syncResult && (
                <div className="flex flex-col items-center py-8">
                  <Loader2 className="animate-spin text-blue-600 mb-4" size={48} />
                  <p className="text-gray-600">Syncing entries to Jira...</p>
                </div>
              )}

              {syncResult && (
                <div>
                  {syncResult.error && !syncResult.results?.length && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg mb-4">
                      <AlertCircle size={18} />
                      <span>{syncResult.error}</span>
                    </div>
                  )}

                  {syncResult.summary && (
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          {syncResult.summary.total}
                        </div>
                        <div className="text-xs text-gray-500">Total</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {syncResult.summary.synced}
                        </div>
                        <div className="text-xs text-green-600">Synced</div>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-yellow-600">
                          {syncResult.summary.skipped}
                        </div>
                        <div className="text-xs text-yellow-600">Skipped</div>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {syncResult.summary.failed}
                        </div>
                        <div className="text-xs text-red-600">Failed</div>
                      </div>
                    </div>
                  )}

                  {syncResult.results?.length > 0 && (
                    <div className="space-y-2">
                      {syncResult.results.map((result, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border ${
                            result.status === 'synced'
                              ? 'border-green-200 bg-green-50'
                              : result.status === 'skipped'
                              ? 'border-yellow-200 bg-yellow-50'
                              : 'border-red-200 bg-red-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {result.status === 'synced' && (
                              <Check className="text-green-600" size={16} />
                            )}
                            {result.status === 'skipped' && (
                              <AlertCircle className="text-yellow-600" size={16} />
                            )}
                            {result.status === 'failed' && (
                              <AlertCircle className="text-red-600" size={16} />
                            )}
                            <span className="font-medium text-gray-900">
                              {result.issueNumber || 'Unknown'}
                            </span>
                            <span className={`text-sm ${
                              result.status === 'synced'
                                ? 'text-green-600'
                                : result.status === 'skipped'
                                ? 'text-yellow-600'
                                : 'text-red-600'
                            }`}>
                              {result.status}
                            </span>
                          </div>
                          {(result.reason || result.error) && (
                            <p className="text-sm text-gray-600 mt-1">
                              {result.reason || result.error}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {syncResult.results?.length === 0 && !syncResult.error && (
                    <p className="text-center text-gray-500 py-4">
                      No entries to sync. Make sure your time entries include Jira issue numbers (e.g., "ABC-123").
                    </p>
                  )}
                </div>
              )}
            </div>

            {!loading && (
              <div className="p-4 border-t bg-gray-50">
                <button
                  onClick={() => setShowSyncModal(false)}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm text-blue-600 font-medium mb-1">Total Time</div>
            <div className="text-2xl font-bold text-gray-900">
              {formatDuration(totalDuration)}
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm text-green-600 font-medium mb-1">Total Entries</div>
            <div className="text-2xl font-bold text-gray-900">
              {entries.length}
            </div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-sm text-purple-600 font-medium mb-1">Projects</div>
            <div className="text-2xl font-bold text-gray-900">
              {reportData.length}
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-md font-semibold text-gray-900 mb-3">Time by Project</h3>
          <div className="space-y-3">
            {reportData.map((stat) => {
              const percentage = (stat.duration / totalDuration) * 100;

              return (
                <div key={stat.project.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: stat.project.color }}
                      ></div>
                      <span className="font-medium text-gray-900">{stat.project.name}</span>
                      <span className="text-sm text-gray-500">({stat.count} entries)</span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900">
                        {formatDuration(stat.duration)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {percentage.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: stat.project.color
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
